import type { OnDestroy, OnInit } from '@angular/core';
import { Component, Input } from '@angular/core';
import { ColumnContext } from '@legacy/common-ui-shared-interfaces';
import { getIsExperienceEditorActive } from '@core/jss-routing';
import { getDroplinkValue, mockForXp } from '@core/jss-utils';
import { isNotNullOrEmptyString } from '@innogy/utils-deprecated';
import type {
  GetReviewsResponseWithStatistics,
  KlantenvertellenLocation,
  Review,
} from '@integration/reviews-api-models';
import { GetReviewsQuery } from '@integration/reviews-api-models';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { getReviews, getReviewsWithStatistics } from '../+state';
import { mockReviewsAndStats } from '../reviews.mocks';
import { ReviewsComponentRendering } from '../reviews.model';

@Component({
  selector: 'wl-review-overview-container',
  templateUrl: './review-overview-container.component.html',
  styleUrls: [],
})
export class ReviewOverviewContainerComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering?: ReviewsComponentRendering;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() context?: ColumnContext;

  public reviews: Review[] = [];

  private currentPage = 1;
  private readonly PAGE_LIMIT = 6;
  private readonly onDestroy$ = new Subject();

  constructor(private readonly store$: Store) {}

  xpEditorActive$ = this.store$.select(getIsExperienceEditorActive);

  reviewsAndStats$ = mockForXp<GetReviewsResponseWithStatistics | undefined>(
    this.xpEditorActive$,
    this.store$.select(getReviewsWithStatistics),
    mockReviewsAndStats
  );

  get location() {
    const input = getDroplinkValue(this.rendering?.fields, 'reviewCategory');
    return isNotNullOrEmptyString(input) ? input : undefined;
  }

  ngOnInit(): void {
    this.store$.dispatch(
      getReviews({
        payload: GetReviewsQuery.fromPlain({
          page: 1,
          amountOfReviews: this.PAGE_LIMIT,
          location: this.location as KlantenvertellenLocation,
        }),
      })
    );

    this.reviewsAndStats$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((reviewsAndStats) => {
        this.reviews = this.reviews.concat(reviewsAndStats?.reviews ?? []);
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  loadMoreReviews(): void {
    this.currentPage += 1;

    this.store$.dispatch(
      getReviews({
        payload: GetReviewsQuery.fromPlain({
          page: this.currentPage,
          amountOfReviews: this.PAGE_LIMIT,
          location: this.location as KlantenvertellenLocation,
        }),
      })
    );
  }

  trackReview(_: number, review: Review): string {
    return review.id;
  }

  get leftReviews(): Review[] {
    return this.reviews.filter((_, index) => index % 2 === 0);
  }

  get rightReviews(): Review[] {
    return this.reviews.filter((_, index) => index % 2 === 1);
  }
}
